import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    consultationType: 'general'
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    en: {
      title: 'Ready to Get Started?',
      subtitle: 'Schedule Your Consultation Today',
      description: 'Contact us today to discuss your legal needs. Our experienced team is ready to provide you with professional legal advice and representation.',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        subject: 'Subject',
        message: 'Tell us about your legal matter',
        consultationType: 'Consultation Type',
        consultationTypes: [
          { value: 'general', label: 'General Consultation' },
          { value: 'corporate', label: 'Corporate Law' },
          { value: 'family', label: 'Family Law' },
          { value: 'property', label: 'Property Law' },
          { value: 'criminal', label: 'Criminal Defense' },
          { value: 'employment', label: 'Employment Law' }
        ],
        submit: 'Request Consultation'
      },
      contact: {
        phone: '+254 721 320 787',
        email: 'info@peterpartnerslaw.co.ke',
        address: 'Nairobi, Kenya',
        hours: 'Mon - Fri: 8:00 AM - 6:00 PM'
      },
      quickActions: {
        call: 'Call Now',
        whatsapp: 'WhatsApp',
        email: 'Send Email'
      }
    },
    sw: {
      title: 'Uko Tayari Kuanza?',
      subtitle: 'Panga Mazungumzo Yako Leo',
      description: 'Wasiliana nasi leo kujadili mahitaji yako ya kisheria. Timu yetu ya uzoefu iko tayari kukupa ushauri wa kitaalamu wa kisheria na uwakilishi.',
      form: {
        name: 'Jina Kamili',
        email: 'Anwani ya Barua Pepe',
        phone: 'Nambari ya Simu',
        subject: 'Mada',
        message: 'Tuambie kuhusu suala lako la kisheria',
        consultationType: 'Aina ya Mazungumzo',
        consultationTypes: [
          { value: 'general', label: 'Mazungumzo ya Jumla' },
          { value: 'corporate', label: 'Sheria ya Makampuni' },
          { value: 'family', label: 'Sheria ya Familia' },
          { value: 'property', label: 'Sheria ya Mali' },
          { value: 'criminal', label: 'Utetezi wa Jinai' },
          { value: 'employment', label: 'Sheria ya Ajira' }
        ],
        submit: 'Omba Mazungumzo'
      },
      contact: {
        phone: '+254 721 320 787',
        email: 'info@peterpartnerslaw.co.ke',
        address: 'Nairobi, Kenya',
        hours: 'Jumatatu - Ijumaa: 8:00 AM - 6:00 PM'
      },
      quickActions: {
        call: 'Piga Simu Sasa',
        whatsapp: 'WhatsApp',
        email: 'Tuma Barua Pepe'
      }
    }
  };

  const currentContent = content[currentLanguage];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    alert(currentLanguage === 'en' ?'Thank you for your consultation request. We will contact you within 24 hours.' :'Asante kwa ombi lako la mazungumzo. Tutawasiliana nawe ndani ya masaa 24.'
    );
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      consultationType: 'general'
    });
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(currentLanguage === 'en' ?'Hello, I would like to schedule a consultation regarding my legal matter.' :'Hujambo, ningependa kupanga mazungumzo kuhusu suala langu la kisheria.');
    window.open(`https://wa.me/254721320787?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.open('tel:+254721320787', '_self');
  };

  const handleEmailClick = () => {
    window.open('mailto:info@peterpartnerslaw.co.ke', '_self');
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-surface/5 to-primary/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-xl font-heading font-semibold text-primary">
            {currentContent.subtitle}
          </p>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {currentContent.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="card p-8">
            <h3 className="text-2xl font-heading font-bold text-text-primary mb-6">
              {currentLanguage === 'en' ? 'Request Consultation' : 'Omba Mazungumzo'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  type="text"
                  name="name"
                  placeholder={currentContent.form.name}
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <Input
                  type="email"
                  name="email"
                  placeholder={currentContent.form.email}
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  type="tel"
                  name="phone"
                  placeholder={currentContent.form.phone}
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
                <select
                  name="consultationType"
                  value={formData.consultationType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                >
                  {currentContent.form.consultationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                type="text"
                name="subject"
                placeholder={currentContent.form.subject}
                value={formData.subject}
                onChange={handleInputChange}
                required
              />

              <textarea
                name="message"
                placeholder={currentContent.form.message}
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                required
              ></textarea>

              <Button
                type="submit"
                variant="primary"
                iconName="Send"
                iconPosition="right"
                fullWidth
              >
                {currentContent.form.submit}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="card p-6">
              <h3 className="text-xl font-heading font-bold text-text-primary mb-4">
                {currentLanguage === 'en' ? 'Quick Contact' : 'Mawasiliano ya Haraka'}
              </h3>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={handleCallClick}
                  iconName="Phone"
                  iconPosition="left"
                  fullWidth
                >
                  {currentContent.quickActions.call}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleWhatsAppClick}
                  iconName="MessageCircle"
                  iconPosition="left"
                  fullWidth
                >
                  {currentContent.quickActions.whatsapp}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleEmailClick}
                  iconName="Mail"
                  iconPosition="left"
                  fullWidth
                >
                  {currentContent.quickActions.email}
                </Button>
              </div>
            </div>

            {/* Contact Details */}
            <div className="card p-6">
              <h3 className="text-xl font-heading font-bold text-text-primary mb-4">
                {currentLanguage === 'en' ? 'Contact Information' : 'Maelezo ya Mawasiliano'}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                    <Icon name="Phone" size={20} color="white" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-text-primary">
                      {currentLanguage === 'en' ? 'Phone' : 'Simu'}
                    </div>
                    <div className="text-text-secondary">{currentContent.contact.phone}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                    <Icon name="Mail" size={20} color="white" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-text-primary">
                      {currentLanguage === 'en' ? 'Email' : 'Barua Pepe'}
                    </div>
                    <div className="text-text-secondary">{currentContent.contact.email}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                    <Icon name="MapPin" size={20} color="white" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-text-primary">
                      {currentLanguage === 'en' ? 'Location' : 'Mahali'}
                    </div>
                    <div className="text-text-secondary">{currentContent.contact.address}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                    <Icon name="Clock" size={20} color="white" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-text-primary">
                      {currentLanguage === 'en' ? 'Office Hours' : 'Masaa ya Ofisi'}
                    </div>
                    <div className="text-text-secondary">{currentContent.contact.hours}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="card p-6">
              <h3 className="text-xl font-heading font-bold text-text-primary mb-4">
                {currentLanguage === 'en' ? 'Our Location' : 'Mahali Petu'}
              </h3>
              <div className="w-full h-64 rounded-lg overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Peter & Partners Law Office Location"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=-1.2921,36.8219&z=14&output=embed"
                  className="border-0"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;